import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FileText,
  FolderKanban,
  MessageSquare,
  AlertCircle,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalView() {
  const { token } = useParams();
  const [model, setModel] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPortal = async () => {
      try {
        // Use direct axios call without credentials for public portal
        const res = await axios.get(`${BACKEND_URL}/api/portal/${token}`);
        setModel(res.data);
      } catch (e) {
        console.error("Portal error:", e);
        if (e.response?.status === 410) {
          setError("This link has expired");
        } else if (e.response?.status === 404) {
          setError("Link not found or has been revoked");
        } else {
          setError(e.response?.data?.detail || "Unable to load portal");
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadPortal();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-indigo-400">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800/50 border-slate-700/50 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-indigo-500 mb-2">Access Denied</h2>
            <p className="text-indigo-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="portal-view">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-indigo-500">ClientKit Portal</h1>
              <p className="text-xs text-indigo-400">Shared Document</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Project Info */}
        {model?.project && (
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                  <FolderKanban className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-indigo-500">{model.project.title}</h2>
                  <Badge className={`mt-1 ${
                    model.project.status === "active" ? "bg-green-500/20 text-green-400" :
                    model.project.status === "completed" ? "bg-blue-500/20 text-indigo-400" :
                    "bg-slate-400 text-indigo-400"
                  }`}>
                    {model.project.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Proposal */}
        {model?.proposal && (
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-400" />
                {model.proposal.title}
                <Badge className="ml-2 bg-indigo-500/20 text-indigo-400">
                  {model.proposal.status}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose pamber-invert max-w-none">
                <pre className="whitespace-pre-wrap text-slate-400 font-sans text-sm leading-relaxed bg-slate-900/50 p-4 rounded-xl">
                  {model.proposal.md_body}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Messages */}
        {model?.messages && model.messages.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-400" />
                Recent Communications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {model.messages.map((msg, i) => (
                  <div key={i} className="p-4 bg-slate-900/30 rounded-xl">
                    <p className="text-sm text-slate-400">{msg.body}</p>
                    {msg.created_at && (
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-8">
          <p className="text-sm text-slate-400">
            Powered by <span className="text-indigo-400">ClientKit</span>
          </p>
        </div>
      </main>
    </div>
  );
}
